import React, { useCallback, useEffect, useMemo, useState } from 'react';

// TruthEngine — standalone "Sovereign" dashboard simulating the
// escalating bounty an Oracle earns for settling a market.
//
//   1. Exponential Auction Engine (bounty)
//        bounty(t) = Base + (Max − Base) · (exp(k·t/T) − 1) / (exp(k) − 1)
//        with Base = 0.5 %, Max = 5.0 %, T = 60 s, k = 3.5.
//        Curve stays below 1.2 % for the first 30 s, then climbs
//        steeply toward the 5.0 % cap. The readout's "pulse" keyframe
//        duration is derived from the normalised bounty progress, so
//        the throb visually accelerates with the number.
//
//   2. Hairdryer Triangulation Monitor
//        Two simulated feeds — Météo-France (CDG) and Sovereign
//        Citizen (Visual). If the Anomaly button fires, CDG spikes
//        +5 °C while Citizen stays flat. Divergence = |CDG − CIT| /
//        CIT × 100. When > 10 %, the dashboard goes Amber Alert:
//        the page glows amber, the auction pauses for "Compliance
//        Audit", and ACCEPT is blocked.
//
//   3. Oracle Interface
//        ACCEPT TASK freezes the live bounty, stops the timer, and
//        prints a Settlement Receipt (fee, verification sources, BST
//        timestamp). RESET returns the whole surface to idle.
//
// Pure visualisation — no backend calls, no wallet, no ledger
// mutation. All data is simulated on the client.

// ── Palette (Sovereign) ──────────────────────────────────────────────
const BG          = '#05070d';
const BG_PANEL    = '#0b1018';
const BG_BLOCK    = '#0f1521';
const RULE        = '#1a2231';
const RULE_SOFT   = '#2b3647';
const INK         = '#e6eaf0';
const INK_MUTED   = '#94a3b8';
const INK_FAINT   = '#55627a';
const GREEN       = '#00FF41';   // Agoracle stable
const AMBER       = '#FFBF00';   // Alert
const AMBER_SOFT  = 'rgba(255,191,0,0.15)';
const RED         = '#ff5a5f';

// ── Auction constants ────────────────────────────────────────────────
const BASE_BOUNTY = 0.5;
const MAX_BOUNTY  = 5.0;
const DURATION    = 60;      // seconds
const URGENCY_K   = 3.5;
const DIV_LIMIT   = 0.10;    // 10 %

const EXP_K       = Math.exp(URGENCY_K);
const EXP_DENOM   = EXP_K - 1;

function computeBounty(elapsed: number): number {
  const t = Math.max(0, Math.min(DURATION, elapsed));
  const tau = t / DURATION;
  const curve = (Math.exp(URGENCY_K * tau) - 1) / EXP_DENOM;
  return BASE_BOUNTY + (MAX_BOUNTY - BASE_BOUNTY) * curve;
}

// Normalised bounty progress in [0, 1] — used for pulse tempo + glow
// intensity, so visuals co-vary with the economic urgency.
function progress(elapsed: number): number {
  const b = computeBounty(elapsed);
  return Math.max(0, Math.min(1, (b - BASE_BOUNTY) / (MAX_BOUNTY - BASE_BOUNTY)));
}

// ── Time formatting ──────────────────────────────────────────────────
function bstStamp(d: Date): string {
  // en-GB in Europe/London resolves to BST (summer) or GMT (winter).
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    hour:     '2-digit',
    minute:   '2-digit',
    second:   '2-digit',
    day:      '2-digit',
    month:    '2-digit',
    year:     'numeric',
    hour12:   false,
    timeZoneName: 'short',
  }).formatToParts(d);
  const pick = (t: string): string => (parts.find(p => p.type === t) || { value: '' }).value;
  return `${pick('day')}/${pick('month')}/${pick('year')} · ${pick('hour')}:${pick('minute')}:${pick('second')} ${pick('timeZoneName')}`;
}

// ── Root component ───────────────────────────────────────────────────
interface Receipt {
  pct:            number;
  at:             Date;
  verification:   string[];
  elapsed:        number;
}

export default function TruthEngine(): React.JSX.Element {
  const [elapsed,  setElapsed]  = useState(0);
  const [running,  setRunning]  = useState(false);
  const [accepted, setAccepted] = useState<Receipt | null>(null);

  // Hairdryer state
  const [cdg,  setCdg]  = useState(20.0);
  const [cit,  setCit]  = useState(20.0);
  const [anomaly, setAnomaly] = useState(false);

  // Divergence → Amber Alert
  const divergence = cit > 0 ? Math.abs(cdg - cit) / cit : 0;
  const amberAlert = divergence > DIV_LIMIT;

  // Live bounty (frozen when accepted)
  const liveBounty   = accepted ? accepted.pct : computeBounty(elapsed);
  const liveProgress = accepted ? (accepted.pct - BASE_BOUNTY) / (MAX_BOUNTY - BASE_BOUNTY) : progress(elapsed);

  // Pulse period: 2.0 s at idle → 0.30 s at max. Derived, not
  // timed by a separate clock, so the pulse can never drift out of
  // phase with the readout.
  const pulseSec = (2.0 - 1.7 * liveProgress).toFixed(2);

  // Master tick — 10 Hz. Auction advances only while running,
  // unpaused, and unaccepted; the compliance audit (amber alert)
  // freezes the timer without resetting elapsed.
  useEffect(() => {
    if (!running || accepted || amberAlert) return;
    const id = window.setInterval(() => {
      setElapsed(prev => {
        const next = prev + 0.1;
        if (next >= DURATION) {
          setRunning(false);
          return DURATION;
        }
        return next;
      });
    }, 100);
    return () => window.clearInterval(id);
  }, [running, accepted, amberAlert]);

  // Hairdryer stream driver — Citizen drifts slowly around 20 °C,
  // CDG matches unless the Anomaly is live (then sits at Citizen + 5).
  useEffect(() => {
    const id = window.setInterval(() => {
      setCit(prev => {
        const drift = (20 - prev) * 0.05 + (Math.random() - 0.5) * 0.3;
        return Math.max(15, Math.min(25, prev + drift));
      });
      setCdg(prev => {
        // Soft-track Citizen with a small noise band; anomaly forces
        // a constant +5 spike applied to the latest Citizen reading.
        const floor = anomaly ? 25 : 20;
        const drift = (floor - prev) * 0.12 + (Math.random() - 0.5) * 0.25;
        return Math.max(15, Math.min(32, prev + drift));
      });
    }, 500);
    return () => window.clearInterval(id);
  }, [anomaly]);

  const startAuction = useCallback(() => {
    setAccepted(null);
    setElapsed(0);
    setRunning(true);
  }, []);

  const resetAll = useCallback(() => {
    setRunning(false);
    setElapsed(0);
    setAccepted(null);
    setAnomaly(false);
  }, []);

  const triggerAnomaly = useCallback(() => setAnomaly(true), []);
  const dismissAnomaly = useCallback(() => setAnomaly(false), []);

  const acceptTask = useCallback(() => {
    if (amberAlert || accepted) return;
    setAccepted({
      pct:           computeBounty(elapsed),
      at:            new Date(),
      elapsed,
      verification: [
        'Météo-France · CDG Sensor',
        'Sovereign Citizen · Visual Check',
      ],
    });
    setRunning(false);
  }, [elapsed, amberAlert, accepted]);

  return (
    <div style={{
      minHeight:  '100vh',
      background: BG,
      color:      INK,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      padding:    '24px 32px 48px',
      boxSizing:  'border-box',
      // Amber screen glow — inset border pulses when divergence
      // exceeds the Compliance threshold.
      boxShadow:  amberAlert ? `inset 0 0 140px 0 ${AMBER_SOFT}, inset 0 0 0 2px ${AMBER}` : 'inset 0 0 0 1px ' + RULE,
      transition: 'box-shadow 0.3s ease',
    }}>
      <style>{`
        @keyframes te-bounty-pulse {
          0%, 100% { transform: scale(1);    text-shadow: 0 0 10px currentColor; }
          50%      { transform: scale(1.04); text-shadow: 0 0 22px currentColor, 0 0 44px currentColor; }
        }
        @keyframes te-amber-pulse {
          0%, 100% { box-shadow: 0 0 0 0   rgba(255,191,0,0.6); }
          50%      { box-shadow: 0 0 0 10px rgba(255,191,0,0);   }
        }
        @keyframes te-curve-sweep {
          from { stroke-dashoffset: 400; }
          to   { stroke-dashoffset: 0;   }
        }
        .te-btn {
          font-family:  inherit;
          font-size:    11px;
          letter-spacing: 0.24em;
          font-weight:  700;
          padding:      12px 20px;
          border-radius: 4px;
          cursor:       pointer;
          background:   ${BG_BLOCK};
          border:       1px solid ${RULE_SOFT};
          color:        ${INK};
          transition:   background 0.15s, border-color 0.15s, box-shadow 0.15s;
        }
        .te-btn:hover     { background: #141d2e; border-color: ${GREEN}; }
        .te-btn:disabled  { opacity: 0.35; cursor: not-allowed; }
        .te-btn.primary   {
          background: rgba(0,255,65,0.08);
          border-color: ${GREEN};
          color: ${GREEN};
          box-shadow: 0 0 18px rgba(0,255,65,0.18);
        }
        .te-btn.primary:hover { background: rgba(0,255,65,0.16); box-shadow: 0 0 28px rgba(0,255,65,0.30); }
        .te-btn.warn      {
          background: rgba(255,191,0,0.08);
          border-color: ${AMBER};
          color: ${AMBER};
        }
        .te-btn.warn:hover { background: rgba(255,191,0,0.18); }
        .te-btn.danger    { border-color: ${RED}; color: ${RED}; }
        .te-btn.danger:hover { background: rgba(255,90,95,0.14); }
      `}</style>

      <Header amberAlert={amberAlert} accepted={!!accepted} />

      <main style={{
        display:             'grid',
        gridTemplateColumns: 'minmax(320px, 1fr) minmax(420px, 1.3fr) minmax(320px, 1fr)',
        gap:                 24,
        marginTop:           24,
      }}>
        <AuctionEnginePanel
          elapsed={elapsed}
          running={running}
          amberAlert={amberAlert}
          accepted={!!accepted}
          onStart={startAuction}
          onReset={resetAll}
        />
        <TruthBountyPanel
          liveBounty={liveBounty}
          liveProgress={liveProgress}
          pulseSec={pulseSec}
          amberAlert={amberAlert}
          accepted={accepted}
          running={running}
          elapsed={elapsed}
          onAccept={acceptTask}
        />
        <HairdryerPanel
          cdg={cdg}
          cit={cit}
          divergence={divergence}
          amberAlert={amberAlert}
          anomaly={anomaly}
          onTrigger={triggerAnomaly}
          onDismiss={dismissAnomaly}
        />
      </main>

      {accepted && <SettlementReceiptPanel receipt={accepted} onReset={resetAll} />}
    </div>
  );
}

// ── Header ───────────────────────────────────────────────────────────
function Header({ amberAlert, accepted }: { amberAlert: boolean; accepted: boolean }): React.JSX.Element {
  const [clock, setClock] = useState(() => bstStamp(new Date()));
  useEffect(() => {
    const id = window.setInterval(() => setClock(bstStamp(new Date())), 1000);
    return () => window.clearInterval(id);
  }, []);
  const status = accepted
    ? { label: 'SETTLED',           tone: GREEN }
    : amberAlert
      ? { label: 'COMPLIANCE AUDIT', tone: AMBER }
      : { label: 'NOMINAL',          tone: GREEN };
  return (
    <header style={{
      display:         'flex',
      alignItems:      'center',
      justifyContent:  'space-between',
      paddingBottom:   16,
      borderBottom:    `1px solid ${RULE}`,
    }}>
      <div>
        <div style={{ fontSize: 10, letterSpacing: '0.34em', color: INK_FAINT, fontWeight: 700 }}>
          AGORACLE · SOVEREIGN ORACLE DESK
        </div>
        <div style={{ fontSize: 24, letterSpacing: '0.10em', color: GREEN, fontWeight: 800, marginTop: 6, textShadow: `0 0 18px ${GREEN}44` }}>
          TRUTH ENGINE
        </div>
      </div>
      <div style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
        <Metric label="STATUS"    value={status.label} tone={status.tone} />
        <Metric label="URGENCY k" value={URGENCY_K.toFixed(2)} tone={INK} />
        <Metric label="WINDOW"    value={`${DURATION}s`} tone={INK} />
        <Metric label="BST"       value={clock} tone={AMBER} mono wide />
      </div>
    </header>
  );
}

function Metric({
  label, value, tone, mono, wide,
}: { label: string; value: string; tone: string; mono?: boolean; wide?: boolean }): React.JSX.Element {
  return (
    <div style={{ textAlign: 'right', minWidth: wide ? 260 : undefined }}>
      <div style={{ fontSize: 8, letterSpacing: '0.28em', color: INK_FAINT, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 13, color: tone, fontWeight: 700, marginTop: 3, fontVariantNumeric: mono ? 'tabular-nums' : undefined }}>
        {value}
      </div>
    </div>
  );
}

// ── Shared panel chrome ──────────────────────────────────────────────
function Panel({
  title, subtitle, accent, children,
}: { title: string; subtitle: string; accent: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <section style={{
      background:    BG_PANEL,
      border:        `1px solid ${RULE}`,
      borderTop:     `2px solid ${accent}`,
      borderRadius:  6,
      padding:       '18px 20px 20px',
      display:       'flex',
      flexDirection: 'column',
      gap:           14,
      minHeight:     520,
    }}>
      <header>
        <div style={{ fontSize: 8, letterSpacing: '0.34em', color: INK_FAINT, fontWeight: 700 }}>
          SIM · {subtitle}
        </div>
        <div style={{ fontSize: 17, letterSpacing: '0.08em', color: accent, fontWeight: 800, marginTop: 4 }}>
          {title}
        </div>
      </header>
      {children}
    </section>
  );
}

// ── 1. Auction Engine Panel ─────────────────────────────────────────
function AuctionEnginePanel({
  elapsed, running, amberAlert, accepted, onStart, onReset,
}: {
  elapsed:     number;
  running:     boolean;
  amberAlert:  boolean;
  accepted:    boolean;
  onStart:     () => void;
  onReset:     () => void;
}): React.JSX.Element {
  const remaining = Math.max(0, DURATION - elapsed);
  const pct       = Math.min(100, (elapsed / DURATION) * 100);

  return (
    <Panel title="AUCTION ENGINE" subtitle="EXPONENTIAL BOUNTY CURVE" accent={GREEN}>
      {/* Parameter table */}
      <div style={{
        background:   BG_BLOCK,
        border:       `1px solid ${RULE}`,
        borderRadius: 4,
        padding:      '10px 14px',
        display:      'grid',
        gridTemplateColumns: '1fr auto',
        rowGap:       6,
        columnGap:    16,
        fontSize:     11,
      }}>
        <Param label="BASE BOUNTY"   value={`${BASE_BOUNTY.toFixed(2)}%`} />
        <Param label="MAX BOUNTY"    value={`${MAX_BOUNTY.toFixed(2)}%`} />
        <Param label="DURATION"      value={`${DURATION}s`} />
        <Param label="URGENCY (k)"   value={URGENCY_K.toFixed(2)} />
      </div>

      {/* Curve SVG */}
      <CurveChart elapsed={elapsed} />

      {/* Clock */}
      <div style={{
        display:        'flex',
        justifyContent: 'space-between',
        fontSize:       11,
        letterSpacing:  '0.22em',
        color:          INK_FAINT,
        fontWeight:     700,
      }}>
        <span>ELAPSED</span>
        <span style={{ color: amberAlert ? AMBER : GREEN, fontVariantNumeric: 'tabular-nums' }}>
          {elapsed.toFixed(1)}s / {DURATION}s
        </span>
      </div>
      <div style={{ height: 6, background: BG_BLOCK, border: `1px solid ${RULE}`, borderRadius: 3, overflow: 'hidden' }}>
        <div style={{
          width:      `${pct}%`,
          height:     '100%',
          background: amberAlert ? AMBER : GREEN,
          boxShadow:  amberAlert ? `0 0 10px ${AMBER}` : `0 0 10px ${GREEN}`,
          transition: 'width 0.2s linear, background 0.3s ease',
        }} />
      </div>
      <div style={{ fontSize: 10, color: INK_MUTED }}>
        Remaining: <span style={{ color: INK, fontVariantNumeric: 'tabular-nums' }}>{remaining.toFixed(1)}s</span>
        {amberAlert && <span style={{ color: AMBER, marginLeft: 10 }}>· TIMER HELD</span>}
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
        <button className="te-btn primary" onClick={onStart} disabled={running && !accepted}>
          {elapsed > 0 && !accepted ? 'RESTART' : 'START AUCTION'}
        </button>
        <button className="te-btn" onClick={onReset}>RESET</button>
      </div>
    </Panel>
  );
}

function Param({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <>
      <span style={{ color: INK_FAINT, letterSpacing: '0.18em', fontSize: 10, fontWeight: 700 }}>{label}</span>
      <span style={{ color: INK, fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{value}</span>
    </>
  );
}

function CurveChart({ elapsed }: { elapsed: number }): React.JSX.Element {
  const W = 320;
  const H = 120;
  const PAD = 8;
  const path = useMemo(() => {
    const points: string[] = [];
    const steps = 80;
    for (let i = 0; i <= steps; i += 1) {
      const t = (i / steps) * DURATION;
      const b = computeBounty(t);
      const x = PAD + ((W - PAD * 2) * i) / steps;
      const y = PAD + (H - PAD * 2) * (1 - (b - BASE_BOUNTY) / (MAX_BOUNTY - BASE_BOUNTY));
      points.push(`${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`);
    }
    return points.join(' ');
  }, []);

  const curX = PAD + ((W - PAD * 2) * elapsed) / DURATION;
  const curY = PAD + (H - PAD * 2) * (1 - (computeBounty(elapsed) - BASE_BOUNTY) / (MAX_BOUNTY - BASE_BOUNTY));

  return (
    <div style={{
      background:   BG_BLOCK,
      border:       `1px solid ${RULE}`,
      borderRadius: 4,
      padding:      8,
    }}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block' }}>
        {/* Gridlines — 30 s midline + bounty halves */}
        <line x1={PAD + (W - PAD*2)/2} y1={PAD} x2={PAD + (W - PAD*2)/2} y2={H - PAD}
              stroke={RULE_SOFT} strokeDasharray="2 4" />
        <line x1={PAD} y1={PAD + (H - PAD*2)/2} x2={W - PAD} y2={PAD + (H - PAD*2)/2}
              stroke={RULE_SOFT} strokeDasharray="2 4" />
        <path d={path} fill="none" stroke={GREEN} strokeWidth={1.6}
              style={{ filter: `drop-shadow(0 0 3px ${GREEN})` }} />
        {/* Live position marker */}
        <circle cx={curX} cy={curY} r={3.5} fill={AMBER}
                style={{ filter: `drop-shadow(0 0 4px ${AMBER})` }} />
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 8, color: INK_FAINT, letterSpacing: '0.16em' }}>
        <span>t = 0</span>
        <span>30s · flat zone ends</span>
        <span>60s</span>
      </div>
    </div>
  );
}

// ── 2. Truth Bounty Panel ───────────────────────────────────────────
function TruthBountyPanel({
  liveBounty, liveProgress, pulseSec, amberAlert, accepted, running, elapsed, onAccept,
}: {
  liveBounty:   number;
  liveProgress: number;
  pulseSec:     string;
  amberAlert:   boolean;
  accepted:     Receipt | null;
  running:      boolean;
  elapsed:      number;
  onAccept:     () => void;
}): React.JSX.Element {
  const canAccept = !amberAlert && !accepted && elapsed > 0;
  const tone = accepted ? GREEN : amberAlert ? AMBER : liveProgress > 0.7 ? AMBER : GREEN;

  return (
    <Panel title="TRUTH BOUNTY" subtitle="ORACLE SETTLEMENT INCENTIVE" accent={accepted ? GREEN : amberAlert ? AMBER : GREEN}>
      <div style={{
        background:   '#000',
        border:       `1px solid ${amberAlert ? AMBER : RULE}`,
        borderRadius: 6,
        padding:      '36px 20px 32px',
        textAlign:    'center',
        position:     'relative',
        boxShadow:    amberAlert ? `inset 0 0 60px ${AMBER}33` : `inset 0 0 60px ${GREEN}22`,
        transition:   'box-shadow 0.3s ease, border-color 0.3s ease',
        flex:         '0 0 auto',
      }}>
        <div style={{ fontSize: 10, letterSpacing: '0.32em', color: INK_FAINT, fontWeight: 700 }}>
          {accepted ? 'FROZEN · SETTLEMENT FEE' : amberAlert ? 'HELD · COMPLIANCE AUDIT' : 'LIVE BOUNTY'}
        </div>
        <div
          style={{
            fontSize:       72,
            fontWeight:     900,
            color:          tone,
            letterSpacing:  '0.02em',
            lineHeight:     1,
            marginTop:      14,
            fontVariantNumeric: 'tabular-nums',
            display:        'inline-block',
            animation:      accepted || amberAlert ? 'none' : `te-bounty-pulse ${pulseSec}s ease-in-out infinite`,
          }}
        >
          {liveBounty.toFixed(2)}%
        </div>
        <div style={{ marginTop: 10, fontSize: 11, letterSpacing: '0.24em', color: INK_MUTED }}>
          {running && !amberAlert && !accepted && <>PULSE {pulseSec}s · PROGRESS {(liveProgress * 100).toFixed(0)}%</>}
          {!running && !accepted && !amberAlert && elapsed === 0 && <>AWAITING START</>}
          {!running && !accepted && !amberAlert && elapsed > 0 && elapsed < DURATION && <>PAUSED</>}
          {!running && !accepted && !amberAlert && elapsed >= DURATION && <>WINDOW EXPIRED</>}
          {amberAlert && <>AUCTION HELD FOR COMPLIANCE AUDIT</>}
          {accepted && <>ACCEPTED · SEE SETTLEMENT RECEIPT BELOW</>}
        </div>
      </div>

      {/* Accept */}
      <button
        className="te-btn primary"
        style={{
          width:        '100%',
          padding:      '20px 18px',
          fontSize:     14,
          letterSpacing: '0.28em',
          marginTop:    'auto',
        }}
        onClick={onAccept}
        disabled={!canAccept}
      >
        ⌬ ACCEPT TASK @ {liveBounty.toFixed(2)}%
      </button>
      <div style={{ fontSize: 10, color: INK_FAINT, textAlign: 'center' }}>
        {!canAccept && amberAlert && 'Amber Alert · dismiss anomaly before accepting'}
        {!canAccept && !amberAlert && !accepted && elapsed === 0 && 'Start the auction to unlock ACCEPT'}
        {!canAccept && accepted && 'Task already settled · RESET to run again'}
        {canAccept && 'Freezes the bounty and generates a Settlement Receipt'}
      </div>
    </Panel>
  );
}

// ── 3. Hairdryer Panel ──────────────────────────────────────────────
function HairdryerPanel({
  cdg, cit, divergence, amberAlert, anomaly, onTrigger, onDismiss,
}: {
  cdg: number; cit: number; divergence: number; amberAlert: boolean;
  anomaly: boolean; onTrigger: () => void; onDismiss: () => void;
}): React.JSX.Element {
  return (
    <Panel title="HAIRDRYER PROTOCOL" subtitle="TRIANGULATION MONITOR" accent={amberAlert ? AMBER : GREEN}>
      {/* Alert bar */}
      <div style={{
        background:   amberAlert ? 'rgba(255,191,0,0.12)' : BG_BLOCK,
        border:       `1px solid ${amberAlert ? AMBER : RULE}`,
        borderLeft:   `2px solid ${amberAlert ? AMBER : RULE_SOFT}`,
        borderRadius: 4,
        padding:      '10px 14px',
        animation:    amberAlert ? 'te-amber-pulse 1.6s ease-in-out infinite' : 'none',
      }}>
        <div style={{ fontSize: 9, letterSpacing: '0.26em', color: amberAlert ? AMBER : INK_FAINT, fontWeight: 700 }}>
          {amberAlert ? 'AMBER ALERT · DIVERGENCE' : 'TRIANGULATION COHERENT'}
        </div>
        <div style={{ fontSize: 12, color: amberAlert ? AMBER : INK_MUTED, marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>
          Δ = {(divergence * 100).toFixed(1)}%{amberAlert ? ` · > ${(DIV_LIMIT * 100).toFixed(0)}% tolerance` : ` · within ${(DIV_LIMIT * 100).toFixed(0)}% tolerance`}
        </div>
      </div>

      {/* Feed rows */}
      <FeedRow
        label="MÉTÉO-FRANCE"
        sublabel="CDG SENSOR · INSTITUTIONAL"
        value={cdg}
        tone={amberAlert ? AMBER : GREEN}
        flagged={amberAlert}
      />
      <FeedRow
        label="SOVEREIGN CITIZEN"
        sublabel="VISUAL CHECK"
        value={cit}
        tone={GREEN}
        flagged={false}
      />

      {/* Controls */}
      <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
        <button className="te-btn warn" onClick={onTrigger} disabled={anomaly}>
          TRIGGER HAIRDRYER ANOMALY
        </button>
        <button className="te-btn" onClick={onDismiss} disabled={!anomaly}>
          DISMISS
        </button>
      </div>

      <div style={{ fontSize: 10, color: INK_FAINT, lineHeight: 1.5 }}>
        Anomaly adds a fixed +5 °C bias to the CDG feed. Divergence is<br />
        |CDG − CITIZEN| / CITIZEN. When it crosses {(DIV_LIMIT * 100).toFixed(0)} %, the Auction is<br />
        held and ACCEPT is blocked until the anomaly is dismissed.
      </div>
    </Panel>
  );
}

function FeedRow({
  label, sublabel, value, tone, flagged,
}: { label: string; sublabel: string; value: number; tone: string; flagged: boolean }): React.JSX.Element {
  return (
    <div style={{
      background:   BG_BLOCK,
      border:       `1px solid ${flagged ? AMBER : RULE}`,
      borderLeft:   `2px solid ${tone}`,
      borderRadius: 4,
      padding:      '12px 14px',
      display:      'grid',
      gridTemplateColumns: '1fr auto',
      alignItems:   'center',
      gap:          12,
    }}>
      <div>
        <div style={{ fontSize: 11, color: tone, fontWeight: 700, letterSpacing: '0.14em' }}>{label}</div>
        <div style={{ fontSize: 9, color: INK_FAINT, marginTop: 2, letterSpacing: '0.22em' }}>{sublabel}</div>
      </div>
      <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        <div style={{ fontSize: 20, color: tone, fontWeight: 700 }}>{value.toFixed(2)}°C</div>
      </div>
    </div>
  );
}

// ── Settlement Receipt ──────────────────────────────────────────────
function SettlementReceiptPanel({
  receipt, onReset,
}: { receipt: Receipt; onReset: () => void }): React.JSX.Element {
  return (
    <section style={{
      marginTop:    24,
      background:   '#000',
      border:       `1px solid ${GREEN}`,
      borderTop:    `2px solid ${GREEN}`,
      borderRadius: 6,
      padding:      '22px 26px 24px',
      boxShadow:    `0 0 32px rgba(0,255,65,0.18), inset 0 0 40px rgba(0,255,65,0.08)`,
    }}>
      <div style={{
        display:         'flex',
        justifyContent:  'space-between',
        alignItems:      'center',
        marginBottom:    14,
      }}>
        <div>
          <div style={{ fontSize: 9, letterSpacing: '0.34em', color: INK_FAINT, fontWeight: 700 }}>
            SOVEREIGN RECEIPT · TRUTH ENGINE
          </div>
          <div style={{ fontSize: 18, letterSpacing: '0.10em', color: GREEN, fontWeight: 800, marginTop: 4, textShadow: `0 0 12px ${GREEN}77` }}>
            SETTLEMENT CONFIRMED
          </div>
        </div>
        <button className="te-btn" onClick={onReset}>NEW TASK</button>
      </div>

      <div style={{
        display:             'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap:                 18,
      }}>
        <ReceiptField label="FINAL FEE" value={`${receipt.pct.toFixed(2)}%`} tone={GREEN} big />
        <ReceiptField
          label="ACCEPTED AT"
          value={`t+${receipt.elapsed.toFixed(1)}s / ${DURATION}s`}
          tone={INK}
        />
        <ReceiptField
          label="BST TIMESTAMP"
          value={bstStamp(receipt.at)}
          tone={AMBER}
          mono
        />
      </div>

      <div style={{
        marginTop:    18,
        padding:      '12px 14px',
        background:   BG_BLOCK,
        border:       `1px dashed ${GREEN}`,
        borderRadius: 4,
      }}>
        <div style={{ fontSize: 9, letterSpacing: '0.28em', color: INK_FAINT, fontWeight: 700 }}>
          VERIFICATION SOURCES
        </div>
        <ul style={{
          margin:     '6px 0 0',
          padding:    0,
          listStyle:  'none',
          fontSize:   12,
          color:      INK,
          lineHeight: 1.7,
        }}>
          {receipt.verification.map((v, i) => (
            <li key={i} style={{ display: 'flex', gap: 8 }}>
              <span style={{ color: GREEN }}>✓</span>
              <span>{v}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function ReceiptField({
  label, value, tone, big, mono,
}: { label: string; value: string; tone: string; big?: boolean; mono?: boolean }): React.JSX.Element {
  return (
    <div>
      <div style={{ fontSize: 9, letterSpacing: '0.28em', color: INK_FAINT, fontWeight: 700 }}>{label}</div>
      <div style={{
        fontSize:           big ? 28 : 14,
        color:              tone,
        fontWeight:         big ? 800 : 700,
        marginTop:          5,
        fontVariantNumeric: mono ? 'tabular-nums' : undefined,
      }}>
        {value}
      </div>
    </div>
  );
}
